/**
 * FEAT-016 (Session 8f.11) — empty-registry redirect helper.
 *
 * Extracted from App.tsx so the branch can be unit-tested without spinning
 * up the React tree. Returns true iff:
 *   - URL pathname is NOT itself `/welcome` (BUG-011 defence-in-depth —
 *     if the SPA somehow loads at `/welcome` because of a stale cache,
 *     service worker, or static-asset miss, we MUST NOT redirect there
 *     again or Safari trips "too many redirects"), AND
 *   - URL has no `from` query param (breaks the redirect loop when the
 *     landing's "Launch app" CTA appends ?from=welcome), AND
 *   - the registry has zero saved stations.
 *
 * Any failure reading the registry (private mode, storage blocked, transient
 * error) returns false so the user gets the in-app registry rather than a
 * hung redirect.
 */
import type { StationRepository } from '@/persistence/ports';

export interface RedirectContext {
  /** The current URL — `window.location.href` in prod, a stub in tests. */
  readonly href: string;
  /** Source of truth for whether the user has any saved stations. */
  readonly repo: Pick<StationRepository, 'list'>;
}

export async function shouldRedirectToWelcome(
  ctx: RedirectContext,
): Promise<boolean> {
  let url: URL;
  try {
    url = new URL(ctx.href);
  } catch {
    return false;
  }
  // BUG-011 — defensive pathname guard. If we are ALREADY on /welcome
  // (or any /welcome/* subpath), do not propose another redirect to
  // /welcome — that loops in Safari with "Too many redirects". This
  // can happen on a misconfigured deploy where the SPA is served at
  // /welcome instead of the marketing HTML.
  if (url.pathname === '/welcome' || url.pathname.startsWith('/welcome/')) {
    return false;
  }
  if (url.searchParams.has('from')) return false;
  try {
    const stations = await ctx.repo.list();
    return stations.length === 0;
  } catch {
    return false;
  }
}
